/**
 * Sohbetin göstermesini istediği karşılaştırma.
 *
 * Canlı olay `{ productIds, max }` taşır. Geçmiş aynı listeyi
 * `productComparison` kaydında `{ products, max }` olarak tutar.
 * İki üründen azı karşılaştırma değildir; buton çıkmaz.
 */
export interface ProductComparisonSnapshot {
  productIds: string[];
  max: number;
}

export function parseProductComparison(
  raw: unknown,
): ProductComparisonSnapshot | null {
  if (typeof raw !== "object" || raw === null) return null;
  const record = raw as {
    productIds?: unknown;
    products?: unknown;
    max?: unknown;
  };
  const source = Array.isArray(record.productIds)
    ? record.productIds
    : record.products;
  if (!Array.isArray(source)) return null;
  const productIds = source
    .filter((id): id is string => typeof id === "string")
    .map((id) => id.trim())
    .filter((id) => id !== "");
  if (productIds.length < 2) return null;
  const max =
    typeof record.max === "number" &&
    Number.isFinite(record.max) &&
    record.max >= 2
      ? Math.floor(record.max)
      : productIds.length;
  return { productIds, max };
}

export interface ComparedAttribute {
  name: string;
  value: string;
}

export interface ComparedProduct {
  productId: string;
  offerId: string;
  name: string;
  brand: string;
  seller: string;
  imageUrl: string;
  price: number;
  listPrice: number;
  inStock: boolean;
  attributes: ComparedAttribute[];
}

/** Tam olarak bir ürün en ucuzsa onun indeksi. Beraberlikte rozet yok. */
export function cheaperIndex(prices: readonly number[]): number | null {
  const priced = prices
    .map((price, index) => ({ price, index }))
    .filter((item) => item.price > 0);
  if (priced.length < 2) return null;
  const lowest = Math.min(...priced.map((item) => item.price));
  const winners = priced.filter((item) => item.price === lowest);
  return winners.length === 1 ? winners[0].index : null;
}

/** Özellik satırları, ilk görülen sırayla. Boş hücre o üründe özellik yok demek. */
export function alignAttributes(
  products: readonly { attributes: readonly ComparedAttribute[] }[],
): { name: string; values: string[] }[] {
  const order: string[] = [];
  const maps = products.map((product) => {
    const values = new Map<string, string>();
    for (const attribute of product.attributes) {
      const name = attribute.name.trim();
      const value = attribute.value.trim();
      if (name === "" || value === "") continue;
      if (!values.has(name)) values.set(name, value);
      if (!order.includes(name)) order.push(name);
    }
    return values;
  });
  return order.map((name) => ({
    name,
    values: maps.map((values) => values.get(name) ?? ""),
  }));
}
